import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Modal } from 'antd';
import { Document, Page } from 'react-pdf';
import { Select, Button, Spin, message, Input, Tooltip } from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  EditOutlined,
  FontSizeOutlined,
  CalendarOutlined,
  FormOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  CommentOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CompressOutlined,
  ExpandOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import axios from '../../utility/axios';
import { useAuth } from '../../hooks/useAuth';
import CommentEditor from '../attachment/CommentEditor';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import '../../styles/documenso/SignaturePlacementStep.css';
import '../../styles/documenso/SignaturePlacementModal.css';

import { pdfjs } from 'react-pdf';
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FIELD_TYPE_KEYS = {
  SIGNATURE: 'documenso.fieldTypes.signature',
  INITIALS: 'documenso.fieldTypes.initials',
  DATE: 'documenso.fieldTypes.date',
  TEXT: 'documenso.fieldTypes.text',
};

const FIELD_TYPE_ICONS = {
  SIGNATURE: EditOutlined,
  INITIALS: FontSizeOutlined,
  DATE: CalendarOutlined,
  TEXT: FormOutlined,
};

const FIELD_DEFAULTS = {
  SIGNATURE: { width: 25, height: 10 },
  INITIALS: { width: 15, height: 6 },
  DATE: { width: 20, height: 6 },
  TEXT: { width: 40, height: 8 },
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (s) => typeof s === 'string' && s.trim() && emailRegex.test(s.trim());

const COMMENT_DEFAULTS = { width: 25, height: 10 };
const POSTIT_MOVE_SIZE = { width: 10, height: 8 };
const isContentEmpty = (html) => !html || (typeof html === 'string' && html.replace(/<[^>]*>/g, '').trim() === '');

const AUTHOR_COLORS = [
  { bg: 'rgba(24, 144, 255, 0.12)', border: '#1890ff', accent: '#1890ff' },
  { bg: 'rgba(82, 196, 26, 0.12)', border: '#52c41a', accent: '#52c41a' },
  { bg: 'rgba(250, 173, 20, 0.15)', border: '#faad14', accent: '#d48806' },
  { bg: 'rgba(245, 34, 45, 0.12)', border: '#f5222d', accent: '#cf1322' },
  { bg: 'rgba(114, 46, 209, 0.12)', border: '#722ed1', accent: '#722ed1' },
  { bg: 'rgba(13, 202, 240, 0.12)', border: '#0dcaf0', accent: '#0891b2' },
  { bg: 'rgba(255, 77, 79, 0.12)', border: '#ff4d4f', accent: '#ff7875' },
  { bg: 'rgba(19, 194, 194, 0.12)', border: '#13c2c2', accent: '#36cfc9' },
  { bg: 'rgba(250, 84, 28, 0.12)', border: '#fa541c', accent: '#fa8c16' },
  { bg: 'rgba(114, 46, 209, 0.1)', border: '#9254de', accent: '#b37feb' },
];

const hashAuthor = (str) => {
  if (!str || typeof str !== 'string') return 0;
  let h = 0;
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h) + str.charCodeAt(i) | 0;
  return Math.abs(h);
};

const getAuthorColor = (comment) => {
  const key = comment?.author_email || comment?.author || 'anonymous';
  const idx = hashAuthor(String(key).toLowerCase()) % AUTHOR_COLORS.length;
  return AUTHOR_COLORS[idx];
};

const findInTree = (list, id) => {
  if (!list) return null;
  for (const c of list) {
    if (c.unique_identifier === id) return c;
    const found = findInTree(c.replies, id);
    if (found) return found;
  }
  return null;
};

const filterFromTree = (tree, id) =>
  tree.filter((c) => c.unique_identifier !== id).map((c) => ({ ...c, replies: filterFromTree(c.replies || [], id) }));

const addReplyToTree = (tree, parentId, newReply) =>
  tree.map((c) => {
    if (c.unique_identifier === parentId) return { ...c, replies: [...(c.replies || []), newReply] };
    if (c.replies?.length) return { ...c, replies: addReplyToTree(c.replies, parentId, newReply) };
    return c;
  });

const renderThreadContent = (thread, borderColor) => {
  const renderItem = (c, depth) => (
    <div
      key={c.unique_identifier}
      className="signature-placement-modal-postit-item"
      style={{ paddingLeft: depth * 14, ...(depth === 0 ? { borderLeft: 'none' } : {}) }}
    >
      <div
        className="signature-placement-modal-postit-content"
        style={depth === 0 ? { color: borderColor } : undefined}
        dangerouslySetInnerHTML={{ __html: c.content || '' }}
      />
      {depth > 0 && (
        <div className="signature-placement-modal-postit-author">
          {(c.author_email || c.author || 'Anonymous').split('@')[0]}
        </div>
      )}
      {(c.replies ?? []).map((r) => renderItem(r, depth + 1))}
    </div>
  );
  return (
    <div className="signature-placement-modal-postit-body">
      {renderItem(thread, 0)}
    </div>
  );
};

const SignaturePlacementModal = ({
  open,
  onClose,
  file,
  fileIndex = 0,
  signerEmails = [],
  value = {},
  onChange,
  onAddRecipient,
  disabled = false,
  draftFileComments,
  onDraftFileCommentsChange,
  localComments,
  onLocalCommentsChange,
  documentFileId,
  documentFileSource,
  documentFileComments,
  onDocumentFileCommentsChange,
}) => {
  const { t } = useTranslation();
  const { user, isLoggedIn } = useAuth();
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSigner, setSelectedSigner] = useState(null);
  const [selectedFieldType, setSelectedFieldType] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addRecipientInput, setAddRecipientInput] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [dragPreview, setDragPreview] = useState(null);
  const [cursorPreview, setCursorPreview] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [moving, setMoving] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const drawStartRef = useRef(null);
  const resizeStartRef = useRef(null);
  const moveStartRef = useRef(null);
  const movePendingRef = useRef(null);
  const containerRef = useRef(null);
  const pageWrapperRef = useRef(null);
  const draggingFieldTypeRef = useRef(null);

  const [placingComment, setPlacingComment] = useState(false);
  const [pendingPlacement, setPendingPlacement] = useState(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentMoving, setCommentMoving] = useState(false);
  const [commentResizing, setCommentResizing] = useState(false);
  const placeholderInputRef = useRef(null);
  const editingInputRef = useRef(null);
  const commentMoveStartRef = useRef(null);
  const commentResizeStartRef = useRef(null);
  const commentDrawStartRef = useRef(null);
  const pendingDocumentPersistRef = useRef(null);
  const draggingCommentRef = useRef(false);
  const markerMovedRef = useRef(false);
  const didFocusPlaceholderRef = useRef(false);
  const rafScheduledRef = useRef(false);
  const pendingCommentCursorRef = useRef(null);
  const pendingCommentPositionRef = useRef(null);
  const pendingCommentPlacementRef = useRef(null);
  const pendingCommentResizeRef = useRef(null);
  const updateCommentPositionRef = useRef(null);
  const updatePendingPlacementRef = useRef(null);
  const updateEditingDraftRef = useRef(null);
  const [commentDragPreview, setCommentDragPreview] = useState(null);
  const [replyingToCommentId, setReplyingToCommentId] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [postitsVisible, setPostitsVisible] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [pageDimensions, setPageDimensions] = useState(null);
  const pageDimensionsRef = useRef(null);
  const [selectedPlacement, setSelectedPlacement] = useState(false);
  const [documentFileCommentsInternal, setDocumentFileCommentsInternal] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const commentsPanelRef = useRef(null);
  const threadRefsMap = useRef({});
  const pdfBlobUrlRef = useRef(null);

  const isDraftFile = !!file?.isDraftFile;
  const useDraftFileComments = isDraftFile && typeof onDraftFileCommentsChange === 'function';
  const useDocumentFileComments = !isDraftFile && !!documentFileId && !!documentFileSource && typeof onDocumentFileCommentsChange === 'function';
  const useLocalComments = !isDraftFile && !useDocumentFileComments && !!file && typeof onLocalCommentsChange === 'function';
  const canComment = useDraftFileComments || useDocumentFileComments || useLocalComments;
  const canReplyToComment = canComment;
  const comments = useDraftFileComments
    ? (Array.isArray(draftFileComments) ? draftFileComments : [])
    : useDocumentFileComments
      ? documentFileCommentsInternal
      : useLocalComments
        ? (Array.isArray(localComments) ? localComments : [])
        : [];
  const currentUserEmail = user?.email ?? null;
  const canEditComment = useCallback((c) => {
    if (useLocalComments || useDraftFileComments) return true;
    if (!currentUserEmail || !c?.author_email) return false;
    return String(c.author_email).toLowerCase() === String(currentUserEmail).toLowerCase();
  }, [useLocalComments, useDraftFileComments, currentUserEmail]);

  const commentTree = useMemo(() => {
    if (!comments.length) return [];
    if (comments.some((c) => Array.isArray(c.replies))) return comments;
    const topLevel = comments.filter((c) => !c.parent_comment_id);
    const byParent = {};
    comments.forEach((c) => {
      if (c.parent_comment_id) {
        const pid = c.parent_comment_id;
        if (!byParent[pid]) byParent[pid] = [];
        byParent[pid].push(c);
      }
    });
    const buildReplies = (parentId) =>
      (byParent[parentId] || []).sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')).map((c) => ({ ...c, replies: buildReplies(c.unique_identifier) }));
    return topLevel.map((c) => ({ ...c, replies: buildReplies(c.unique_identifier) }));
  }, [comments]);

  const currentPageComments = comments.filter((c) => Number(c.page) === currentPage);
  const currentPageCommentTree = commentTree.filter((c) => Number(c.page) === currentPage);
  const canViewComments = canComment;

  const hasSigners = signerEmails.length > 0;

  const getFieldTypeIcon = useCallback((type) => {
    const IconComponent = FIELD_TYPE_ICONS[type];
    return IconComponent ? <IconComponent /> : null;
  }, []);

  const getFieldTypeLabel = useCallback(
    (type) => t(FIELD_TYPE_KEYS[type]) || type,
    [t]
  );

  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 3;
  const ZOOM_STEP = 0.2;

  const handleZoom = useCallback((delta) => {
    setZoomLevel((z) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z + delta)));
  }, []);

  useEffect(() => {
    if (open) {
      setSidebarCollapsed(true);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setZoomLevel(1);
      setPlacingComment(false);
      setPendingPlacement(null);
      setCommentDraft('');
      setSelectedCommentId(null);
      setEditingDraft(null);
      setReplyingToCommentId(null);
      setReplyDraft('');
      setSelectedPlacement(false);
      setCommentDragPreview(null);
      setDocumentFileCommentsInternal([]);
      setError(null);
      setMinimized(false);
      setPageDimensions(null);
      pageDimensionsRef.current = null;
      pendingDocumentPersistRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (!useDocumentFileComments) {
      setDocumentFileCommentsInternal([]);
    }
  }, [useDocumentFileComments]);

  useEffect(() => {
    if (!open || !useDocumentFileComments || !documentFileId) return;
    let cancelled = false;
    setCommentsLoading(true);
    axios.get('/api/attachment-preview/comments/', { params: { attachment_id: documentFileId, source: documentFileSource } })
      .then((res) => {
        if (!cancelled) {
          const fetched = res.data.comments || [];
          setDocumentFileCommentsInternal(fetched);
          onDocumentFileCommentsChange?.(fetched);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDocumentFileCommentsInternal([]);
          onDocumentFileCommentsChange?.([]);
        }
      })
      .finally(() => {
        if (!cancelled) setCommentsLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, useDocumentFileComments, documentFileId, documentFileSource, onDocumentFileCommentsChange]);

  useEffect(() => {
    if (open && signerEmails.length > 0 && !selectedSigner) {
      setSelectedSigner(signerEmails[0]);
    }
  }, [open, signerEmails]);

  useEffect(() => {
    if (!selectedSigner) setSelectedFieldType(null);
  }, [selectedSigner]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !open) return;
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        handleZoom(delta);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [open, handleZoom]);

  const baseScale = typeof window !== 'undefined' ? Math.min(1.5, (window.devicePixelRatio || 1.25)) : 1.25;
  const pdfScale = baseScale * zoomLevel;

  const signatureFields = value?.signatureFields || {};
  const setSignatureFields = (fn) => {
    const next = typeof fn === 'function' ? fn(signatureFields) : fn;
    onChange?.({ ...value, signatureFields: next });
  };

  useEffect(() => {
    if (!open || !file) {
      setPdfData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setPdfData(null);
      try {
        let arrayBuffer;
        if (file.isDraftFile && file.download_url) {
          const res = await axios.get(file.download_url, { responseType: 'arraybuffer' });
          if (cancelled) return;
          const ext = (file.name || '').split('.').pop()?.toLowerCase();
          const isPdf = ext === 'pdf' || res.headers?.['content-type']?.includes('pdf');
          if (isPdf) {
            arrayBuffer = res.data;
          } else {
            const rawBlob = new Blob([res.data]);
            const fd = new FormData();
            fd.append('file', rawBlob, file.name || 'document');
            const convertRes = await axios.post('/api/docuso/convert-for-preview/', fd, {
              responseType: 'arraybuffer',
            });
            if (cancelled) return;
            arrayBuffer = convertRes.data;
          }
        } else if ((file.documentFileId || documentFileId) && (file.documentFileSource || documentFileSource) === 'documenso') {
          const docId = file.documentFileId || documentFileId;
          const res = await axios.get(`/api/docuso/download/${docId}/`, { responseType: 'arraybuffer' });
          if (cancelled) return;
          arrayBuffer = res.data;
        } else {
          const f = file.originFileObj || file;
          if (!f || typeof (f.arrayBuffer || f.slice) !== 'function') {
            throw new Error('Invalid file: missing File/Blob (originFileObj or file with arrayBuffer)');
          }
          const isPdf = (f.type || '').includes('application/pdf') || (f.name || '').toLowerCase().endsWith('.pdf');
          if (isPdf) {
            arrayBuffer = await f.arrayBuffer();
          } else {
            const fd = new FormData();
            fd.append('file', f, f.name || file.name || 'document');
            const res = await axios.post('/api/docuso/convert-for-preview/', fd, {
              responseType: 'arraybuffer',
            });
            if (cancelled) return;
            arrayBuffer = res.data;
          }
        }
        if (cancelled) return;
        if (pdfBlobUrlRef.current) {
          URL.revokeObjectURL(pdfBlobUrlRef.current);
          pdfBlobUrlRef.current = null;
        }
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        pdfBlobUrlRef.current = url;
        setPdfData(url);
      } catch (err) {
        if (!cancelled) {
          const status = err.response?.status;
          const data = err.response?.data;
          let errMsg = err.message;
          if (data && typeof data === 'object' && typeof data.error === 'string') errMsg = data.error;
          else if (typeof data === 'string') errMsg = data;
          else if (data instanceof ArrayBuffer) {
            try {
              const parsed = JSON.parse(new TextDecoder().decode(data));
              if (parsed?.error) errMsg = parsed.error;
            } catch (_) {}
          }
          setError({ status, message: errMsg });
          message.error(t('documenso.errors.pdfLoadFailed') || 'Failed to load PDF');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (pdfBlobUrlRef.current) {
        URL.revokeObjectURL(pdfBlobUrlRef.current);
        pdfBlobUrlRef.current = null;
      }
    };
  }, [open, file, documentFileId, documentFileSource, t]);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setCurrentPage(1);
  }, []);

  const measurePageDimensions = useCallback(() => {
    if (pageWrapperRef.current && pdfData) {
      const rect = pageWrapperRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const w = Math.round(rect.width);
        const h = Math.round(rect.height);
        if (!pageDimensionsRef.current || pageDimensionsRef.current.width !== w || pageDimensionsRef.current.height !== h) {
          pageDimensionsRef.current = { width: w, height: h };
          setPageDimensions({ width: w, height: h });
        }
      }
    }
  }, [pdfData]);

  useEffect(() => {
    if (!pdfData || error || loading) return;
    const runMeasure = () => {
      measurePageDimensions();
    };
    const timer = setTimeout(runMeasure, 500);
    const resizeTimer = setTimeout(runMeasure, 800);
    const ro = pageWrapperRef.current && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => setTimeout(runMeasure, 100))
      : null;
    if (ro && pageWrapperRef.current) ro.observe(pageWrapperRef.current);
    const onResize = () => runMeasure();
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      clearTimeout(resizeTimer);
      ro?.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [pdfData, error, loading, currentPage, zoomLevel, measurePageDimensions]);

  useEffect(() => {
    if (minimized) setSidebarCollapsed(true);
  }, [minimized]);

  const placeFieldAtPosition = useCallback(
    (fieldType, positionX, positionY, width, height) => {
      if (!selectedSigner || disabled) return;
      const def = FIELD_DEFAULTS[fieldType] || FIELD_DEFAULTS.SIGNATURE;
      const w = width ?? def.width;
      const h = height ?? def.height;
      const allForFile = Object.values(signatureFields).flat().filter((f) => f.identifier === fileIndex);
      const maxOrder = allForFile.reduce((m, f) => Math.max(m, f.order ?? -1), -1);
      const field = {
        identifier: fileIndex,
        type: fieldType,
        page: currentPage,
        positionX,
        positionY,
        width: Math.max(5, Math.min(90, w)),
        height: Math.max(3, Math.min(50, h)),
        order: maxOrder + 1,
      };
      if (fieldType === 'TEXT') {
        field.fieldMeta = { label: '', placeholder: '', type: 'text' };
      }
      const existing = signatureFields[selectedSigner] || [];
      setSignatureFields({
        ...signatureFields,
        [selectedSigner]: [...existing, field],
      });
      message.success(t('documenso.signaturePlaced') || 'Field placed');
    },
    [disabled, selectedSigner, currentPage, fileIndex, signatureFields, setSignatureFields, t]
  );

  const placeFieldAndClearSelection = useCallback(
    (fieldType, positionX, positionY, width, height) => {
      placeFieldAtPosition(fieldType, positionX, positionY, width, height);
      setSelectedFieldType(null);
    },
    [placeFieldAtPosition]
  );

  const updateFieldDimensions = useCallback(
    (email, index, updates) => {
      const existing = signatureFields[email] || [];
      const field = existing[index];
      if (!field) return;
      const next = [...existing];
      next[index] = { ...field, ...updates };
      const nextAll = { ...signatureFields, [email]: next };
      setSignatureFields(nextAll);
    },
    [signatureFields, setSignatureFields]
  );

  const getPositionFromEvent = useCallback((e, pageEl) => {
    const rect = pageEl.getBoundingClientRect();
    const x = (e.clientX ?? e.touches?.[0]?.clientX ?? 0) - rect.left;
    const y = (e.clientY ?? e.touches?.[0]?.clientY ?? 0) - rect.top;
    const displayWidth = rect.width;
    const displayHeight = rect.height;
    return {
      positionX: Math.max(0, Math.min(100, (x / displayWidth) * 100)),
      positionY: Math.max(0, Math.min(100, (y / displayHeight) * 100)),
    };
  }, []);

  const fieldsMatch = useCallback((a, b) => {
    if (!a || !b) return false;
    return (
      a.identifier === b.identifier &&
      a.page === b.page &&
      a.positionX === b.positionX &&
      a.positionY === b.positionY &&
      a.type === b.type &&
      (a.order === b.order || (a.order == null && b.order == null))
    );
  }, []);

  const RESIZE_HANDLE_TOLERANCE = 18;
  const hitTestFieldAtPosition = useCallback((posX, posY) => {
    const fieldsOnPage = Object.entries(signatureFields).flatMap(([email, fields]) =>
      (fields || [])
        .filter((f) => f.identifier === fileIndex && f.page === currentPage)
        .map((f) => {
          const all = signatureFields[email] || [];
          const idx = all.findIndex((x) => x === f || fieldsMatch(x, f));
          return { email, index: idx, field: f };
        })
    );
    for (const { email, index, field } of fieldsOnPage) {
      const px = field.positionX ?? field.position_x ?? 0;
      const py = field.positionY ?? field.position_y ?? 0;
      const w = field.width || 25;
      const h = field.height || 10;
      const inBox = posX >= px && posX <= px + w && posY >= py && posY <= py + h;
      if (!inBox) continue;
      const isSelected = selectedField?.email === email && selectedField?.index === index;
      if (isSelected && !disabled) {
        const tolX = Math.max(RESIZE_HANDLE_TOLERANCE, w * 0.4);
        const tolY = Math.max(RESIZE_HANDLE_TOLERANCE, h * 0.4);
        if (posX - px <= tolX && posY - py <= tolY) return { email, index, field, hit: 'tl' };
        if (px + w - posX <= tolX && posY - py <= tolY) return { email, index, field, hit: 'tr' };
        if (posX - px <= tolX && py + h - posY <= tolY) return { email, index, field, hit: 'bl' };
        if (px + w - posX <= tolX && py + h - posY <= tolY) return { email, index, field, hit: 'br' };
      }
      return { email, index, field, hit: 'body' };
    }
    return null;
  }, [signatureFields, fileIndex, currentPage, selectedField, disabled, fieldsMatch]);

  const getCommentPositionFromEvent = useCallback((e) => {
    const pageEl = pageWrapperRef.current;
    if (!pageEl) return null;
    const { positionX, positionY } = getPositionFromEvent(e, pageEl);
    return { position_x: positionX, position_y: positionY };
  }, [getPositionFromEvent]);

  const placeCommentBox = useCallback((position_x, position_y, width, height) => {
    setPendingPlacement({
      page: currentPage,
      position_x,
      position_y,
      width: Math.max(5, Math.min(90, width)),
      height: Math.max(3, Math.min(50, height)),
    });
    setPlacingComment(false);
    setSelectedPlacement(true);
  }, [currentPage]);

  const updatePendingPlacement = useCallback((updates) => {
    setPendingPlacement((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  const updateEditingDraft = useCallback((updates) => {
    setEditingDraft((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  const updateCommentPosition = useCallback((commentId, updates) => {
    const updateInTree = (tree, id, u) =>
      tree.map((c) => (c.unique_identifier === id ? { ...c, ...u } : { ...c, replies: updateInTree(c.replies || [], id, u) }));
    if (useLocalComments) {
      const list = Array.isArray(localComments) ? localComments : [];
      onLocalCommentsChange(list.map((c) => (c.unique_identifier === commentId ? { ...c, ...updates } : c)));
      return;
    }
    if (useDraftFileComments) {
      const list = Array.isArray(draftFileComments) ? draftFileComments : [];
      onDraftFileCommentsChange(list.map((c) => (c.unique_identifier === commentId ? { ...c, ...updates } : c)));
      return;
    }
    if (useDocumentFileComments) {
      const next = updateInTree(documentFileCommentsInternal, commentId, updates);
      setDocumentFileCommentsInternal(next);
      onDocumentFileCommentsChange?.(next);
    }
  }, [useLocalComments, localComments, onLocalCommentsChange, useDraftFileComments, draftFileComments, onDraftFileCommentsChange, useDocumentFileComments, documentFileCommentsInternal, onDocumentFileCommentsChange]);

  const handleAddComment = useCallback(() => {
    const content = typeof commentDraft === 'string' ? commentDraft.trim() : commentDraft;
    if (isContentEmpty(content) || !canComment || commentSubmitting) return;
    const placement = pendingPlacement || {
      page: currentPage,
      position_x: 50,
      position_y: 50,
      width: COMMENT_DEFAULTS.width,
      height: COMMENT_DEFAULTS.height,
    };
    if (useDocumentFileComments) {
      setCommentSubmitting(true);
      axios.post('/api/attachment-preview/comments/create/', {
        attachment_id: documentFileId,
        source: documentFileSource,
        content,
        page: placement.page,
        position_x: placement.position_x,
        position_y: placement.position_y,
        width: placement.width,
        height: placement.height,
      }, { headers: { 'Content-Type': 'application/json' } })
        .then((res) => {
          const newComment = { ...res.data.comment, replies: [] };
          const next = [...documentFileCommentsInternal, newComment];
          setDocumentFileCommentsInternal(next);
          onDocumentFileCommentsChange?.(next);
          setCommentDraft('');
          setPendingPlacement(null);
          setSelectedPlacement(false);
          message.success(t('common.saved') || 'Comment added');
        })
        .catch((err) => message.error(err.response?.data?.error || err.message))
        .finally(() => setCommentSubmitting(false));
      return;
    }
    const newComment = {
      content,
      page: placement.page,
      position_x: placement.position_x,
      position_y: placement.position_y,
      width: placement.width,
      height: placement.height,
      unique_identifier: `${isDraftFile ? 'draft' : 'local'}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      replies: [],
    };
    if (useDraftFileComments) {
      onDraftFileCommentsChange([...comments, newComment]);
    } else {
      onLocalCommentsChange([...comments, newComment]);
    }
    setCommentDraft('');
    setPendingPlacement(null);
    setSelectedPlacement(false);
    message.success(t('common.saved') || 'Comment added');
  }, [commentDraft, canComment, commentSubmitting, pendingPlacement, currentPage, useDraftFileComments, useDocumentFileComments, useLocalComments, comments, documentFileCommentsInternal, onDraftFileCommentsChange, onDocumentFileCommentsChange, onLocalCommentsChange, documentFileId, documentFileSource, isDraftFile, t]);

  const handleDiscardPlacement = useCallback(() => {
    setPendingPlacement(null);
    setCommentDraft('');
    setSelectedPlacement(false);
  }, []);

  const handleSaveEditedComment = useCallback((options = {}) => {
    const { closeOnSuccess = false, silent = false } = options;
    if (!editingDraft || !selectedCommentId || commentSubmitting) return;
    const content = typeof editingDraft.content === 'string' ? editingDraft.content.trim() : editingDraft.content;
    if (isContentEmpty(content)) return;
    const updatedComment = { content, position_x: editingDraft.position_x, position_y: editingDraft.position_y, width: editingDraft.width, height: editingDraft.height };
    const updateFlat = (list) => list.map((c) => (c.unique_identifier === selectedCommentId ? { ...c, ...updatedComment } : c));
    if (useDocumentFileComments) {
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selectedCommentId);
      if (!isUuid) {
        const next = updateFlat(documentFileCommentsInternal);
        setDocumentFileCommentsInternal(next);
        onDocumentFileCommentsChange?.(next);
        if (closeOnSuccess) {
          setSelectedCommentId(null);
          setEditingDraft(null);
        }
        if (!silent) message.success(t('common.saved') || 'Saved');
        return;
      }
      setCommentSubmitting(true);
      const comment = findInTree(commentTree, selectedCommentId) || documentFileCommentsInternal.find((c) => c.unique_identifier === selectedCommentId);
      axios.put(`/api/attachment-preview/comments/${selectedCommentId}/`, {
        content,
        page: editingDraft.page ?? comment?.page ?? 1,
        position_x: editingDraft.position_x,
        position_y: editingDraft.position_y,
        width: editingDraft.width,
        height: editingDraft.height,
      }, { headers: { 'Content-Type': 'application/json' } })
        .then((res) => {
          const next = documentFileCommentsInternal.map((c) => (c.unique_identifier === selectedCommentId ? res.data.comment : c));
          setDocumentFileCommentsInternal(next);
          onDocumentFileCommentsChange?.(next);
          if (closeOnSuccess) {
            setSelectedCommentId(null);
            setEditingDraft(null);
          }
          if (!silent) message.success(t('common.saved') || 'Saved');
        })
        .catch((err) => message.error(err.response?.data?.error || err.message))
        .finally(() => setCommentSubmitting(false));
      return;
    }
    if (useDraftFileComments) onDraftFileCommentsChange(updateFlat(comments));
    else onLocalCommentsChange(updateFlat(comments));
    if (closeOnSuccess) {
      setSelectedCommentId(null);
      setEditingDraft(null);
    }
    if (!silent) message.success(t('common.saved') || 'Saved');
  }, [editingDraft, selectedCommentId, commentSubmitting, comments, documentFileCommentsInternal, commentTree, useDraftFileComments, useDocumentFileComments, useLocalComments, onDraftFileCommentsChange, onDocumentFileCommentsChange, onLocalCommentsChange, t]);

  const collectCommentAndDescendantIds = useCallback((tree, targetId) => {
    const ids = new Set();
    const target = findInTree(tree, targetId);
    if (target) {
      const collect = (c) => { ids.add(c.unique_identifier); (c.replies || []).forEach(collect); };
      collect(target);
    }
    return ids;
  }, []);

  const handleDeleteComment = useCallback((commentId) => {
    const id = commentId ?? selectedCommentId;
    if (!id || commentSubmitting) return;
    const idsToRemove = collectCommentAndDescendantIds(commentTree, id);
    const filtered = idsToRemove.size > 0
      ? comments.filter((c) => !idsToRemove.has(c.unique_identifier))
      : comments.filter((c) => c.unique_identifier !== id);
    if (useDocumentFileComments) {
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      if (!isUuid) {
        setDocumentFileCommentsInternal(filtered);
        onDocumentFileCommentsChange?.(filtered);
        setSelectedCommentId(null);
        setEditingDraft(null);
        message.success(t('common.deleted') || 'Deleted');
        return;
      }
      setCommentSubmitting(true);
      axios.delete(`/api/attachment-preview/comments/${id}/delete/`)
        .then(() => {
          setDocumentFileCommentsInternal(filtered);
          onDocumentFileCommentsChange?.(filtered);
          setSelectedCommentId(null);
          setEditingDraft(null);
          message.success(t('common.deleted') || 'Deleted');
        })
        .catch((err) => message.error(err.response?.data?.error || err.message))
        .finally(() => setCommentSubmitting(false));
      return;
    }
    if (useDraftFileComments) onDraftFileCommentsChange(filtered);
    else onLocalCommentsChange(filtered);
    setSelectedCommentId(null);
    setEditingDraft(null);
    message.success(t('common.deleted') || 'Deleted');
  }, [selectedCommentId, commentSubmitting, comments, commentTree, collectCommentAndDescendantIds, useDraftFileComments, useDocumentFileComments, useLocalComments, onDraftFileCommentsChange, onDocumentFileCommentsChange, onLocalCommentsChange, t]);

  const handleSelectComment = useCallback((c) => {
    if (!canEditComment(c)) return;
    const next = selectedCommentId === c.unique_identifier ? null : c.unique_identifier;
    setSelectedCommentId(next);
    if (next) {
      setReplyingToCommentId(null);
      setEditingDraft({
        content: c.content,
        position_x: c.position_x,
        position_y: c.position_y,
        width: c.width ?? COMMENT_DEFAULTS.width,
        height: c.height ?? COMMENT_DEFAULTS.height,
      });
    } else {
      setEditingDraft(null);
    }
  }, [selectedCommentId, canEditComment]);

  const openReplyTo = useCallback((commentId) => {
    const isOpening = replyingToCommentId !== commentId;
    setReplyingToCommentId((prev) => (prev === commentId ? null : commentId));
    setReplyDraft('');
    if (isOpening) {
      setSelectedCommentId(null);
      setEditingDraft(null);
    }
  }, [replyingToCommentId]);

  const handleAddReply = useCallback((parentCommentId, content) => {
    const trimmed = typeof content === 'string' ? content.trim() : content;
    if (isContentEmpty(trimmed) || !canReplyToComment || commentSubmitting) return;
    const parent = findInTree(commentTree, parentCommentId);
    if (!parent) return;
    const newReply = {
      unique_identifier: `${useDraftFileComments ? 'draft' : 'local'}-reply-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      parent_comment_id: parentCommentId,
      author_email: currentUserEmail || 'Anonymous',
      content: trimmed,
      page: parent.page ?? 1,
      position_x: 50,
      position_y: 50,
      created_at: new Date().toISOString(),
      replies: [],
    };
    if (useDraftFileComments) {
      onDraftFileCommentsChange([...(Array.isArray(draftFileComments) ? draftFileComments : []), newReply]);
      setReplyingToCommentId(null);
      setReplyDraft('');
      message.success(t('attachmentPreview.replyAdded') || 'Reply added');
      return;
    }
    if (useLocalComments) {
      onLocalCommentsChange([...(Array.isArray(localComments) ? localComments : []), newReply]);
      setReplyingToCommentId(null);
      setReplyDraft('');
      message.success(t('attachmentPreview.replyAdded') || 'Reply added');
      return;
    }
    if (!documentFileId || !useDocumentFileComments) return;
    setCommentSubmitting(true);
    axios.post('/api/attachment-preview/comments/create/', {
      attachment_id: documentFileId,
      source: documentFileSource,
      content: trimmed,
      parent_comment_id: parentCommentId,
    }, { headers: { 'Content-Type': 'application/json' } })
      .then((res) => {
        const next = [...documentFileCommentsInternal, res.data.comment];
        setDocumentFileCommentsInternal(next);
        onDocumentFileCommentsChange?.(next);
        setReplyingToCommentId(null);
        setReplyDraft('');
        message.success(t('attachmentPreview.replyAdded') || 'Reply added');
      })
      .catch((err) => message.error(err.response?.data?.error || err.message))
      .finally(() => setCommentSubmitting(false));
  }, [canReplyToComment, commentSubmitting, commentTree, useDraftFileComments, draftFileComments, onDraftFileCommentsChange, useLocalComments, localComments, onLocalCommentsChange, useDocumentFileComments, documentFileId, documentFileSource, documentFileCommentsInternal, onDocumentFileCommentsChange, currentUserEmail, t]);

  useEffect(() => {
    updateCommentPositionRef.current = updateCommentPosition;
    updatePendingPlacementRef.current = updatePendingPlacement;
    updateEditingDraftRef.current = updateEditingDraft;
  }, [updateCommentPosition, updatePendingPlacement, updateEditingDraft]);

  const flushRafCommentUpdates = useCallback(() => {
    if (pendingCommentCursorRef.current !== undefined) {
      setCursorPreview(pendingCommentCursorRef.current);
      pendingCommentCursorRef.current = undefined;
    }
    const pos = pendingCommentPositionRef.current;
    if (pos && updateCommentPositionRef.current) {
      updateCommentPositionRef.current(pos.commentId, pos.updates);
      pendingCommentPositionRef.current = null;
    }
    const pl = pendingCommentPlacementRef.current;
    if (pl) {
      const fn = pl.isEditing ? updateEditingDraftRef.current : updatePendingPlacementRef.current;
      if (fn) fn(pl.updates);
      pendingCommentPlacementRef.current = null;
    }
    const rs = pendingCommentResizeRef.current;
    if (rs) {
      const fn = rs.isEditing ? updateEditingDraftRef.current : updatePendingPlacementRef.current;
      if (fn) fn(rs.updates);
      pendingCommentResizeRef.current = null;
    }
  }, []);

  const scheduleRafCommentFlush = useCallback(() => {
    if (rafScheduledRef.current) return;
    rafScheduledRef.current = true;
    requestAnimationFrame(() => {
      rafScheduledRef.current = false;
      flushRafCommentUpdates();
    });
  }, [flushRafCommentUpdates]);

  useEffect(() => {
    if (pendingPlacement) {
      if (!didFocusPlaceholderRef.current) {
        didFocusPlaceholderRef.current = true;
        const timer = setTimeout(() => placeholderInputRef.current?.focus?.(), 50);
        return () => clearTimeout(timer);
      }
    } else {
      didFocusPlaceholderRef.current = false;
    }
  }, [pendingPlacement]);

  useEffect(() => {
    if (editingDraft && selectedCommentId) {
      const timer = setTimeout(() => editingInputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [editingDraft, selectedCommentId]);

  const handleCommentMarkerMouseDown = useCallback((c, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target.closest('button, .signature-placement-modal-postit-reply-btn')) return;
    if (!canComment || commentSubmitting) return;
    if (!canEditComment(c)) return;
    markerMovedRef.current = false;
    const pos = getCommentPositionFromEvent(e);
    if (!pos) return;
    const w = c.width != null && c.height != null ? c.width : POSTIT_MOVE_SIZE.width;
    const h = c.width != null && c.height != null ? c.height : POSTIT_MOVE_SIZE.height;
    commentMoveStartRef.current = {
      startMouseX: pos.position_x,
      startMouseY: pos.position_y,
      startPosX: c.position_x,
      startPosY: c.position_y,
      movingMarkerId: c.unique_identifier,
      movingMarker: c,
      markerWidth: w,
      markerHeight: h,
    };
    setCommentMoving(true);
  }, [canComment, commentSubmitting, canEditComment, getCommentPositionFromEvent]);

  const handleCommentPlaceholderMouseDown = useCallback((e, isEditing = false) => {
    if (e.target.closest('button, .signature-placement-modal-placeholder-save, .signature-placement-modal-resize-handle, .ProseMirror')) return;
    e.stopPropagation();
    const target = isEditing ? editingDraft : pendingPlacement;
    if (!target) return;
    if (!isEditing) setSelectedPlacement(true);
    const pos = getCommentPositionFromEvent(e);
    if (pos) {
      commentMoveStartRef.current = {
        startMouseX: pos.position_x,
        startMouseY: pos.position_y,
        startPosX: target.position_x,
        startPosY: target.position_y,
        editing: isEditing,
      };
      setCommentMoving(true);
    }
  }, [getCommentPositionFromEvent, pendingPlacement, editingDraft]);

  const handleCommentResizeMouseDown = useCallback((e, corner, isEditing = false) => {
    e.stopPropagation();
    e.preventDefault();
    setCommentResizing(corner);
    const target = isEditing ? editingDraft : pendingPlacement;
    if (target) {
      commentResizeStartRef.current = {
        corner,
        startPosX: target.position_x,
        startPosY: target.position_y,
        startW: target.width,
        startH: target.height,
        editing: isEditing,
      };
    }
  }, [pendingPlacement, editingDraft]);

  const handleFieldBodyMouseDown = useCallback(
    (e, email, index, field) => {
      if (disabled) return;
      e.stopPropagation();
      e.preventDefault();
      setSelectedField({ email, index });
      const pageEl = pageWrapperRef.current;
      if (!pageEl) return;
      const { positionX, positionY } = getPositionFromEvent(e, pageEl);
      movePendingRef.current = {
        email,
        index,
        startMouseX: positionX,
        startMouseY: positionY,
        startPosX: field.positionX ?? field.position_x ?? 0,
        startPosY: field.positionY ?? field.position_y ?? 0,
      };
    },
    [disabled, getPositionFromEvent]
  );

  const handleFieldResizeMouseDown = useCallback(
    (e, email, index, field, corner) => {
      if (disabled) return;
      e.stopPropagation();
      e.preventDefault();
      setSelectedField({ email, index });
      setResizing(true);
      resizeStartRef.current = {
        email,
        index,
        corner,
        startW: field.width || 25,
        startH: field.height || 10,
        startPosX: field.positionX ?? field.position_x ?? 0,
        startPosY: field.positionY ?? field.position_y ?? 0,
      };
    },
    [disabled]
  );

  const handlePageMouseDown = useCallback(
    (e) => {
      if (e.target.closest('.signature-placement-modal-postit-wrapper, .signature-placement-modal-placeholder, .signature-placement-modal-resize-handle, .signature-field-overlay-box.selected')) return;
      if (!pageWrapperRef.current) return;
      const { positionX, positionY } = getPositionFromEvent(e, pageWrapperRef.current);
      const fieldHit = hitTestFieldAtPosition(positionX, positionY);
      if (fieldHit && !disabled) {
        const { email, index, field, hit } = fieldHit;
        setSelectedField({ email, index });
        if (hit === 'body') {
          movePendingRef.current = {
            email,
            index,
            startMouseX: positionX,
            startMouseY: positionY,
            startPosX: field.positionX ?? field.position_x ?? 0,
            startPosY: field.positionY ?? field.position_y ?? 0,
          };
        } else {
          setResizing(true);
          resizeStartRef.current = {
            email,
            index,
            corner: hit,
            startW: field.width || 25,
            startH: field.height || 10,
            startPosX: field.positionX ?? field.position_x ?? 0,
            startPosY: field.positionY ?? field.position_y ?? 0,
          };
        }
        return;
      }
      movePendingRef.current = null;
      setSelectedField(null);
      setSelectedPlacement(false);
      setSelectedCommentId(null);
      setEditingDraft(null);
      if (placingComment && canComment) {
        const pos = getCommentPositionFromEvent(e);
        if (pos) {
          commentDrawStartRef.current = { ...pos };
          setIsDrawing(true);
        }
        return;
      }
      if (disabled || !selectedSigner || !selectedFieldType) return;
      drawStartRef.current = { ...getPositionFromEvent(e, pageWrapperRef.current), fieldType: selectedFieldType };
      setIsDrawing(true);
    },
    [disabled, selectedSigner, selectedFieldType, getPositionFromEvent, placingComment, canComment, getCommentPositionFromEvent, hitTestFieldAtPosition]
  );

  const MOVE_DRAG_THRESHOLD = 0.4;
  const handlePageMouseMove = useCallback(
    (e) => {
      const pageEl = pageWrapperRef.current;
      if (!pageEl) return;

      /* Promote click-to-select into drag-to-move when user drags past threshold */
      const pending = movePendingRef.current;
      if (pending) {
        const { positionX: currX, positionY: currY } = getPositionFromEvent(e, pageEl);
        const deltaX = Math.abs(currX - pending.startMouseX);
        const deltaY = Math.abs(currY - pending.startMouseY);
        if (deltaX > MOVE_DRAG_THRESHOLD || deltaY > MOVE_DRAG_THRESHOLD) {
          moveStartRef.current = { ...pending };
          movePendingRef.current = null;
          setMoving(true);
        }
      }

      /* Field type move/resize - check first to avoid confusion with comment logic */
      if (moveStartRef.current) {
        const { email, index, startMouseX, startMouseY, startPosX, startPosY } = moveStartRef.current;
        const { positionX: currX, positionY: currY } = getPositionFromEvent(e, pageEl);
        const deltaX = currX - startMouseX;
        const deltaY = currY - startMouseY;
        const posX = Math.max(0, Math.min(100, startPosX + deltaX));
        const posY = Math.max(0, Math.min(100, startPosY + deltaY));
        const field = (signatureFields[email] || [])[index];
        if (field) {
          const maxX = 100 - (field.width || 25);
          const maxY = 100 - (field.height || 10);
          updateFieldDimensions(email, index, {
            positionX: Math.max(0, Math.min(maxX, posX)),
            positionY: Math.max(0, Math.min(maxY, posY)),
          });
        }
        return;
      }
      if (resizeStartRef.current) {
        const { email, index, corner, startW, startH, startPosX, startPosY } = resizeStartRef.current;
        const { positionX: currX, positionY: currY } = getPositionFromEvent(e, pageEl);
        let posX = startPosX;
        let posY = startPosY;
        let w = startW;
        let h = startH;
        if (corner === 'br') {
          w = Math.max(5, Math.min(90, currX - startPosX));
          h = Math.max(3, Math.min(50, currY - startPosY));
        } else if (corner === 'bl') {
          posX = Math.max(0, Math.min(startPosX + startW - 5, currX));
          w = Math.max(5, Math.min(90, startPosX + startW - currX));
          h = Math.max(3, Math.min(50, currY - startPosY));
        } else if (corner === 'tr') {
          posY = Math.max(0, Math.min(startPosY + startH - 3, currY));
          w = Math.max(5, Math.min(90, currX - startPosX));
          h = Math.max(3, Math.min(50, startPosY + startH - currY));
        } else if (corner === 'tl') {
          posX = Math.max(0, Math.min(startPosX + startW - 5, currX));
          posY = Math.max(0, Math.min(startPosY + startH - 3, currY));
          w = Math.max(5, Math.min(90, startPosX + startW - currX));
          h = Math.max(3, Math.min(50, startPosY + startH - currY));
        }
        updateFieldDimensions(email, index, { positionX: posX, positionY: posY, width: w, height: h });
        resizeStartRef.current = { email, index, corner, startW: w, startH: h, startPosX: posX, startPosY: posY };
        return;
      }

      const cm = commentMoveStartRef.current;
      if (commentMoving && cm?.movingMarkerId) {
        const pos = getCommentPositionFromEvent(e);
        if (!pos) return;
        const deltaX = pos.position_x - cm.startMouseX;
        const deltaY = pos.position_y - cm.startMouseY;
        if (Math.abs(deltaX) > 0.5 || Math.abs(deltaY) > 0.5) markerMovedRef.current = true;
        const maxX = 100 - (cm.markerWidth ?? COMMENT_DEFAULTS.width);
        const maxY = 100 - (cm.markerHeight ?? COMMENT_DEFAULTS.height);
        const posX = Math.max(0, Math.min(maxX, cm.startPosX + deltaX));
        const posY = Math.max(0, Math.min(maxY, cm.startPosY + deltaY));
        pendingCommentPositionRef.current = { commentId: cm.movingMarkerId, updates: { position_x: posX, position_y: posY } };
        scheduleRafCommentFlush();
        pendingDocumentPersistRef.current = {
          commentId: cm.movingMarkerId,
          position_x: posX,
          position_y: posY,
          width: cm.markerWidth ?? COMMENT_DEFAULTS.width,
          height: cm.markerHeight ?? COMMENT_DEFAULTS.height,
          marker: cm.movingMarker,
        };
        return;
      }
      const activePlacement = cm?.editing ? editingDraft : pendingPlacement;
      const updatePlacement = cm?.editing ? updateEditingDraft : updatePendingPlacement;
      if (commentMoving && cm && activePlacement && !commentResizing) {
        const pos = getCommentPositionFromEvent(e);
        if (!pos) return;
        const deltaX = pos.position_x - cm.startMouseX;
        const deltaY = pos.position_y - cm.startMouseY;
        const maxX = 100 - activePlacement.width;
        const maxY = 100 - activePlacement.height;
        const posX = Math.max(0, Math.min(maxX, cm.startPosX + deltaX));
        const posY = Math.max(0, Math.min(maxY, cm.startPosY + deltaY));
        pendingCommentPlacementRef.current = { isEditing: !!cm.editing, updates: { position_x: posX, position_y: posY } };
        scheduleRafCommentFlush();
        return;
      }
      const cr = commentResizeStartRef.current;
      if (commentResizing && cr && (cr.editing ? editingDraft : pendingPlacement)) {
        const pos = getCommentPositionFromEvent(e);
        if (!pos) return;
        let posX = cr.startPosX, posY = cr.startPosY, w = cr.startW, h = cr.startH;
        if (cr.corner === 'br') {
          w = Math.max(5, Math.min(90, pos.position_x - cr.startPosX));
          h = Math.max(3, Math.min(50, pos.position_y - cr.startPosY));
        } else if (cr.corner === 'bl') {
          posX = Math.max(0, Math.min(cr.startPosX + cr.startW - 5, pos.position_x));
          w = Math.max(5, Math.min(90, cr.startPosX + cr.startW - pos.position_x));
          h = Math.max(3, Math.min(50, pos.position_y - cr.startPosY));
        } else if (cr.corner === 'tr') {
          posY = Math.max(0, Math.min(cr.startPosY + cr.startH - 3, pos.position_y));
          w = Math.max(5, Math.min(90, pos.position_x - cr.startPosX));
          h = Math.max(3, Math.min(50, cr.startPosY + cr.startH - pos.position_y));
        } else if (cr.corner === 'tl') {
          posX = Math.max(0, Math.min(cr.startPosX + cr.startW - 5, pos.position_x));
          posY = Math.max(0, Math.min(cr.startPosY + cr.startH - 3, pos.position_y));
          w = Math.max(5, Math.min(90, cr.startPosX + cr.startW - pos.position_x));
          h = Math.max(3, Math.min(50, cr.startPosY + cr.startH - pos.position_y));
        }
        pendingCommentResizeRef.current = { isEditing: !!cr.editing, updates: { position_x: posX, position_y: posY, width: w, height: h } };
        scheduleRafCommentFlush();
        commentResizeStartRef.current = { ...cr, startPosX: posX, startPosY: posY, startW: w, startH: h };
        return;
      }
      if (isDrawing && commentDrawStartRef.current) {
        const start = commentDrawStartRef.current;
        const pos = getCommentPositionFromEvent(e);
        if (!pos) return;
        const posX = Math.min(start.position_x, pos.position_x);
        const posY = Math.min(start.position_y, pos.position_y);
        const w = Math.abs(pos.position_x - start.position_x);
        const h = Math.abs(pos.position_y - start.position_y);
        setCursorPreview({
          positionX: posX,
          positionY: posY,
          width: Math.max(2, w),
          height: Math.max(2, h),
          isComment: true,
        });
        return;
      }
      if (placingComment) {
        const pos = getCommentPositionFromEvent(e);
        if (pos) {
          setCursorPreview({
            positionX: pos.position_x,
            positionY: pos.position_y,
            width: COMMENT_DEFAULTS.width,
            height: COMMENT_DEFAULTS.height,
            isComment: true,
          });
        }
        return;
      }
      if (!placingComment && !commentMoving && !commentResizing && !(selectedSigner && selectedFieldType)) {
        setCursorPreview(null);
        return;
      }

      if (drawStartRef.current) {
        const start = drawStartRef.current;
        const { positionX: currX, positionY: currY } = getPositionFromEvent(e, pageEl);
        const posX = Math.min(start.positionX, currX);
        const posY = Math.min(start.positionY, currY);
        const w = Math.abs(currX - start.positionX);
        const h = Math.abs(currY - start.positionY);
        setCursorPreview({
          positionX: posX,
          positionY: posY,
          width: Math.max(2, w),
          height: Math.max(2, h),
          fieldType: start.fieldType,
          isDrawing: true,
        });
        return;
      }

      if (!selectedSigner || !selectedFieldType || disabled) {
        setCursorPreview(null);
        return;
      }
      const { positionX, positionY } = getPositionFromEvent(e, pageEl);
      const def = FIELD_DEFAULTS[selectedFieldType] || FIELD_DEFAULTS.SIGNATURE;
      setCursorPreview({ positionX, positionY, width: def.width, height: def.height });
    },
    [selectedSigner, disabled, selectedFieldType, getPositionFromEvent, resizing, moving, signatureFields, updateFieldDimensions, placingComment, commentMoving, commentResizing, editingDraft, pendingPlacement, getCommentPositionFromEvent, updateCommentPosition, updatePendingPlacement, updateEditingDraft, scheduleRafCommentFlush]
  );

  const handlePageMouseUp = useCallback(
    (e) => {
      if (commentMoving || commentResizing) {
        flushRafCommentUpdates();
      }
      if (commentMoving) {
        const cm = commentMoveStartRef.current;
        setCommentMoving(false);
        commentMoveStartRef.current = null;
        if (cm?.editing && editingDraft) {
          handleSaveEditedComment({ closeOnSuccess: true, silent: true });
        } else if (cm?.movingMarkerId && !useDocumentFileComments && !markerMovedRef.current) {
          const marker = cm.movingMarker || findInTree(commentTree, cm.movingMarkerId);
          if (marker) {
            setSelectedCommentId(cm.movingMarkerId);
            setEditingDraft({
              content: marker.content,
              position_x: marker.position_x,
              position_y: marker.position_y,
              width: marker.width ?? COMMENT_DEFAULTS.width,
              height: marker.height ?? COMMENT_DEFAULTS.height,
            });
          }
        } else if (cm?.movingMarkerId && useDocumentFileComments) {
          const pending = pendingDocumentPersistRef.current;
          pendingDocumentPersistRef.current = null;
          if (pending && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(pending.commentId)) {
            const marker = pending.marker || findInTree(commentTree, pending.commentId);
            if (marker) {
              setCommentSubmitting(true);
              axios.put(`/api/attachment-preview/comments/${pending.commentId}/`, {
                content: marker.content,
                page: marker.page ?? 1,
                position_x: pending.position_x,
                position_y: pending.position_y,
                width: pending.width,
                height: pending.height,
              }, { headers: { 'Content-Type': 'application/json' } })
                .then((res) => {
                  const next = documentFileCommentsInternal.map((c) => (c.unique_identifier === pending.commentId ? res.data.comment : c));
                  setDocumentFileCommentsInternal(next);
                  onDocumentFileCommentsChange?.(next);
                })
                .catch((err) => message.error(err.response?.data?.error || err.message))
                .finally(() => setCommentSubmitting(false));
            }
          }
        }
        return;
      }
      if (commentResizing) {
        const cr = commentResizeStartRef.current;
        setCommentResizing(null);
        commentResizeStartRef.current = null;
        if (cr?.editing && editingDraft) {
          handleSaveEditedComment({ closeOnSuccess: true, silent: true });
        }
        return;
      }
      if (commentDrawStartRef.current && pageWrapperRef.current) {
        const start = commentDrawStartRef.current;
        const pos = getCommentPositionFromEvent(e);
        if (pos) {
          const posX = Math.min(start.position_x, pos.position_x);
          const posY = Math.min(start.position_y, pos.position_y);
          let w = Math.abs(pos.position_x - start.position_x);
          let h = Math.abs(pos.position_y - start.position_y);
          if (w >= 2 && h >= 2) {
            placeCommentBox(posX, posY, w, h);
          } else {
            placeCommentBox(posX, posY, COMMENT_DEFAULTS.width, COMMENT_DEFAULTS.height);
          }
        }
        commentDrawStartRef.current = null;
        setIsDrawing(false);
        setCursorPreview(null);
        return;
      }
      movePendingRef.current = null;
      if (moveStartRef.current) {
        setMoving(false);
        moveStartRef.current = null;
        return;
      }
      if (resizeStartRef.current) {
        setResizing(null);
        resizeStartRef.current = null;
        return;
      }
      if (drawStartRef.current && pageWrapperRef.current) {
        const start = drawStartRef.current;
        const { positionX: currX, positionY: currY } = getPositionFromEvent(e, pageWrapperRef.current);
        const posX = Math.min(start.positionX, currX);
        const posY = Math.min(start.positionY, currY);
        let w = Math.abs(currX - start.positionX);
        let h = Math.abs(currY - start.positionY);
        if (w >= 2 && h >= 2) {
          placeFieldAndClearSelection(start.fieldType, posX, posY, w, h);
        } else {
          const def = FIELD_DEFAULTS[start.fieldType] || FIELD_DEFAULTS.SIGNATURE;
          placeFieldAndClearSelection(start.fieldType, posX, posY, def.width, def.height);
        }
        drawStartRef.current = null;
        setIsDrawing(false);
        setCursorPreview(null);
        return;
      }
    },
    [placeFieldAndClearSelection, getPositionFromEvent, resizing, moving, commentMoving, commentResizing, getCommentPositionFromEvent, placeCommentBox, handleSaveEditedComment, useDocumentFileComments, documentFileCommentsInternal, commentTree, onDocumentFileCommentsChange, flushRafCommentUpdates]
  );

  const handleFieldTypeDragStart = useCallback(
    (e, fieldType) => {
      if (!selectedSigner || disabled) {
        e.preventDefault();
        return;
      }
      draggingFieldTypeRef.current = fieldType;
      e.dataTransfer.setData('application/x-field-type', fieldType);
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('text/plain', fieldType);
    },
    [selectedSigner, disabled]
  );

  const handleCommentDragStart = useCallback((e) => {
    if (!canComment || commentSubmitting) {
      e.preventDefault();
      return;
    }
    draggingCommentRef.current = true;
    e.dataTransfer.setData('application/x-comment-marker', 'comment');
    e.dataTransfer.effectAllowed = 'copy';
  }, [canComment, commentSubmitting]);

  const handleCommentDragEnd = useCallback(() => {
    draggingCommentRef.current = false;
    setCommentDragPreview(null);
  }, []);

  const handlePageDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      const pageEl = pageWrapperRef.current;
      if (!pageEl) return;
      if (draggingCommentRef.current && canComment) {
        const pos = getCommentPositionFromEvent(e);
        if (pos) {
          setCommentDragPreview({
            position_x: pos.position_x,
            position_y: pos.position_y,
            width: COMMENT_DEFAULTS.width,
            height: COMMENT_DEFAULTS.height,
          });
        }
        return;
      }
      if (!selectedSigner || disabled) return;
      const fieldType = draggingFieldTypeRef.current;
      if (!fieldType || !['SIGNATURE', 'INITIALS', 'DATE', 'TEXT'].includes(fieldType)) return;
      const { positionX, positionY } = getPositionFromEvent(e, pageEl);
      const def = FIELD_DEFAULTS[fieldType] || FIELD_DEFAULTS.SIGNATURE;
      setDragPreview({ fieldType, positionX, positionY, width: def.width, height: def.height });
    },
    [selectedSigner, disabled, getPositionFromEvent, canComment, getCommentPositionFromEvent]
  );

  const handlePageDragLeave = useCallback((e) => {
    if (!pageWrapperRef.current?.contains(e.relatedTarget)) {
      setDragPreview(null);
      setCommentDragPreview(null);
    }
  }, []);

  const handlePageDrop = useCallback(
    (e) => {
      e.preventDefault();
      const fieldType = e.dataTransfer.getData('application/x-field-type') || e.dataTransfer.getData('text/plain');
      const isCommentDrag = e.dataTransfer.getData('application/x-comment-marker') === 'comment';
      setDragPreview(null);
      setCommentDragPreview(null);
      draggingCommentRef.current = false;
      if (isCommentDrag && canComment) {
        const pos = getCommentPositionFromEvent(e);
        if (pos) {
          placeCommentBox(pos.position_x, pos.position_y, COMMENT_DEFAULTS.width, COMMENT_DEFAULTS.height);
          message.success(t('attachmentPreview.positionSet') || 'Position set');
        }
        return;
      }
      if (!selectedSigner || disabled || !['SIGNATURE', 'INITIALS', 'DATE', 'TEXT'].includes(fieldType)) return;
      const pageEl = pageWrapperRef.current;
      if (!pageEl) return;
      const { positionX, positionY } = getPositionFromEvent(e, pageEl);
      placeFieldAndClearSelection(fieldType, positionX, positionY);
    },
    [selectedSigner, disabled, placeFieldAndClearSelection, getPositionFromEvent, canComment, getCommentPositionFromEvent, placeCommentBox, t]
  );

  const handleFieldTypeDragEnd = useCallback(() => {
    draggingFieldTypeRef.current = null;
    setDragPreview(null);
  }, []);

  const handlePageMouseLeave = useCallback(() => {
    if (!resizing && !commentResizing && !commentMoving) {
      drawStartRef.current = null;
      commentDrawStartRef.current = null;
      setIsDrawing(false);
      setCursorPreview(null);
      setCommentDragPreview(null);
    }
  }, [resizing, commentResizing, commentMoving]);

  useEffect(() => {
    if (!isDrawing && !resizing && !moving && !commentMoving && !commentResizing) return;
    const onMove = (e) => handlePageMouseMove(e);
    const onUp = (e) => handlePageMouseUp(e);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDrawing, resizing, moving, commentMoving, commentResizing, handlePageMouseMove, handlePageMouseUp]);

  const removeField = useCallback(
    (email, index) => {
      const existing = (signatureFields[email] || []).filter((_, i) => i !== index);
      const next = { ...signatureFields };
      if (existing.length) next[email] = existing;
      else delete next[email];
      setSignatureFields(next);
    },
    [signatureFields, setSignatureFields]
  );

  const removeFieldByRef = useCallback(
    (email, field) => {
      const all = signatureFields[email] || [];
      const idx = all.findIndex((x) => x === field || fieldsMatch(x, field));
      if (idx >= 0) removeField(email, idx);
    },
    [signatureFields, removeField, fieldsMatch]
  );

  const fileConfig = useMemo(() => (pdfData ? { url: pdfData } : null), [pdfData]);

  const canMinimize = !!pdfData && !loading && !error && !!pageDimensions;
  const HEADER_HEIGHT = 55;
  const BODY_PADDING_H = 32;
  const BODY_PADDING_V = 32;
  const PAGINATION_HEIGHT = numPages > 1 ? 52 : 0;
  const minimizedContentWidth = pageDimensions
    ? Math.min(Math.ceil(pageDimensions.width) + BODY_PADDING_H + 10, typeof window !== 'undefined' ? window.innerWidth * 0.95 : 800)
    : 800;
  const minimizedContentHeight = pageDimensions
    ? Math.min(
        Math.ceil(pageDimensions.height) + HEADER_HEIGHT + PAGINATION_HEIGHT + BODY_PADDING_V,
        typeof window !== 'undefined' ? window.innerHeight * 0.95 : 600
      )
    : 600;

  const modalStyles = minimized && canMinimize
    ? {
        wrapper: { width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
        content: {
          width: minimizedContentWidth,
          height: minimizedContentHeight,
          maxWidth: '95vw',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          margin: 0,
        },
        body: { flex: 1, minHeight: 0, overflow: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', padding: 16 },
      }
    : {
        wrapper: { height: '100vh', alignItems: 'flex-start' },
        content: { height: '99vh', minHeight: '99vh', maxHeight: '99vh', display: 'flex', flexDirection: 'column', width: '95vw' },
        body: { flex: 1, minHeight: 0, overflow: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column' },
      };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={minimized && canMinimize ? minimizedContentWidth : '95vw'}
      destroyOnClose
      centered={minimized && canMinimize}
      styles={modalStyles}
      title={
        <div className="signature-placement-modal-header" onClick={(e) => e.stopPropagation()}>
          <span className="signature-placement-modal-filename">
            {file ? (file.originFileObj || file).name : (t('documenso.placementTitle') || 'Place signature fields')}
          </span>
          <div className="signature-placement-modal-header-controls">
            {fileConfig && numPages != null && numPages > 1 && (
              <div className="signature-placement-pagination-inline">
                <Button
                  size="small"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  tabIndex={-1}
                >
                  {t('common.previous') || 'Previous'}
                </Button>
                <span className="signature-placement-page-info">
                  {currentPage} / {numPages}
                </span>
                <Button
                  size="small"
                  disabled={currentPage >= numPages}
                  onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
                  tabIndex={-1}
                >
                  {t('common.next') || 'Next'}
                </Button>
              </div>
            )}
            {fileConfig && numPages != null && (
              <div className="signature-placement-zoom signature-placement-zoom-header">
                <Tooltip title={t('documenso.zoomOut') || 'Zoom out (Ctrl+scroll)'}>
                  <Button
                    size="small"
                    icon={<ZoomOutOutlined />}
                    onClick={() => handleZoom(-ZOOM_STEP)}
                    disabled={zoomLevel <= ZOOM_MIN}
                  />
                </Tooltip>
                <span className="signature-placement-zoom-level">{Math.round(zoomLevel * 100)}%</span>
                <Tooltip title={t('documenso.zoomIn') || 'Zoom in (Ctrl+scroll)'}>
                  <Button
                    size="small"
                    icon={<ZoomInOutlined />}
                    onClick={() => handleZoom(ZOOM_STEP)}
                    disabled={zoomLevel >= ZOOM_MAX}
                  />
                </Tooltip>
              </div>
            )}
            {canComment && (
              <div className={`signature-placement-modal-comment-chip-segmented${commentSubmitting ? ' disabled' : ''}`}>
                <div
                  className={`signature-placement-modal-comment-chip${placingComment ? ' selected' : ''}${commentSubmitting ? ' disabled' : ''}`}
                  onClick={() => { if (!commentSubmitting) { setPlacingComment((p) => !p); setSelectedFieldType(null); } }}
                  draggable={!commentSubmitting}
                  onDragStart={handleCommentDragStart}
                  onDragEnd={handleCommentDragEnd}
                  tabIndex={-1}
                >
                  <span>{t('attachmentPreview.addCommentLabel') || 'Add comment'}</span>
                </div>
              </div>
            )}
            {canViewComments && commentTree.length > 0 && (
              <button
                type="button"
                className={`signature-placement-modal-header-btn signature-placement-modal-postits-toggle${postitsVisible ? ' active' : ''}`}
                onClick={() => setPostitsVisible((v) => !v)}
                title={postitsVisible ? (t('attachmentPreview.hidePostits') || 'Hide comment markers') : (t('attachmentPreview.showPostits') || 'Show comment markers')}
                tabIndex={-1}
              >
                {postitsVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              </button>
            )}
            <div className="signature-placement-modal-header-icon-btns">
              {canMinimize && (
                <button
                  type="button"
                  className="signature-placement-modal-header-btn"
                  onClick={() => setMinimized((m) => !m)}
                  title={minimized ? (t('common.maximize') || 'Maximize') : (t('common.minimize') || 'Minimize')}
                  tabIndex={-1}
                >
                  {minimized ? <ExpandOutlined /> : <CompressOutlined />}
                </button>
              )}
              <button
                type="button"
                className="signature-placement-modal-header-btn"
                onClick={onClose}
                title={t('common.close') || 'Close'}
                tabIndex={-1}
              >
                <CloseOutlined />
              </button>
            </div>
          </div>
        </div>
      }
      closeIcon={null}
      className={`signature-placement-modal ${minimized ? 'signature-placement-modal-minimized' : ''}`}
    >
      <div className="signature-placement-content">
        <div className="signature-placement-options">
          <div className="signature-placement-row">
            {hasSigners ? (
              <>
                <span>{t('documenso.selectSigner') || 'Signer:'}</span>
                <Select
                  placeholder={t('documenso.selectSignerPlaceholder') || 'Select who will sign'}
                  value={selectedSigner}
                  onChange={setSelectedSigner}
                  options={signerEmails.map((email) => ({ label: email, value: email }))}
                  style={{ width: 200 }}
                  disabled={disabled}
                  popupClassName="signature-placement-signer-dropdown"
                />
              </>
            ) : (
              <div className="signature-placement-add-recipient">
                <Input
                  placeholder={t('documenso.addRecipientPlaceholder') || 'Add recipient email'}
                  value={addRecipientInput}
                  onChange={(e) => setAddRecipientInput(e.target.value)}
                  onPressEnter={() => {
                    const email = addRecipientInput.trim();
                    if (!email) return;
                    if (!isValidEmail(email)) {
                      message.error(t('messages.validation.invalidEmail', { email }) || `Invalid email: ${email}`);
                      return;
                    }
                    if (onAddRecipient && onAddRecipient(email)) {
                      setSelectedSigner(email);
                      setAddRecipientInput('');
                    } else {
                      message.error(t('documenso.addRecipientFailed') || 'Failed to add recipient');
                    }
                  }}
                  disabled={disabled}
                  style={{ width: 240 }}
                  allowClear
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const email = addRecipientInput.trim();
                    if (!email) return;
                    if (!isValidEmail(email)) {
                      message.error(t('messages.validation.invalidEmail', { email }) || `Invalid email: ${email}`);
                      return;
                    }
                    if (onAddRecipient && onAddRecipient(email)) {
                      setSelectedSigner(email);
                      setAddRecipientInput('');
                    } else {
                      message.error(t('documenso.addRecipientFailed') || 'Failed to add recipient');
                    }
                  }}
                  disabled={disabled || !addRecipientInput.trim()}
                  title={t('documenso.addRecipient') || 'Add recipient'}
                />
              </div>
            )}
          </div>
          <div className="signature-placement-field-types-row">
            <Tooltip title={!selectedSigner ? (t('documenso.mustDefineSigner') || 'Must define a signer') : null}>
              <div className="signature-field-types-wrapper">
                <div className={`signature-field-types-segmented${!selectedSigner ? ' disabled' : ''}`}>
                  {['SIGNATURE', 'INITIALS', 'DATE', 'TEXT'].map((ft) => {
                    const IconComponent = FIELD_TYPE_ICONS[ft];
                    const isSelected = selectedFieldType === ft;
                    const chipDisabled = !selectedSigner || disabled;
                    return (
                      <div
                        key={ft}
                        className={`signature-field-type-chip${isSelected ? ' selected' : ''}${chipDisabled ? ' disabled' : ''}`}
                        onClick={() => { if (!chipDisabled) { setSelectedFieldType(ft); setPlacingComment(false); } }}
                        draggable={!!selectedSigner && !disabled}
                        onDragStart={(e) => { if (chipDisabled) e.preventDefault(); else handleFieldTypeDragStart(e, ft); }}
                        onDragEnd={handleFieldTypeDragEnd}
                      >
                        {IconComponent && <IconComponent />}
                        <span>{t(FIELD_TYPE_KEYS[ft]) || ft}</span>
                        {isSelected && selectedSigner && !disabled && (
                          <Tooltip title={t('common.buttons.remove') || 'Clear selection'}>
                            <span
                              className="signature-field-type-chip-delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFieldType(null);
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedFieldType(null); } }}
                            >
                              <DeleteOutlined />
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    );
                  })}
                </div>
                {!selectedSigner && <div className="signature-field-types-blocker" aria-hidden />}
              </div>
              </Tooltip>
          </div>
        </div>

        <div className="signature-placement-preview" ref={containerRef}>
          {loading ? (
            <div className="signature-placement-loading">
              <Spin size="large" />
            </div>
          ) : error ? (
            <div className="signature-placement-error">
              <p>{error.message || (t('documenso.errors.pdfLoadFailed') || 'Failed to load PDF')}</p>
            </div>
          ) : fileConfig ? (
            <div className="signature-placement-preview-inner">
              {canViewComments && sidebarCollapsed && (
                <button
                  type="button"
                  className="signature-placement-modal-show-comments-btn"
                  onClick={() => setSidebarCollapsed(false)}
                  title={t('attachmentPreview.showComments') || 'Show comments'}
                  tabIndex={-1}
                >
                  <MenuUnfoldOutlined />
                  <span>{t('attachmentPreview.showComments') || 'Show comments'}</span>
                </button>
              )}
              <div className="signature-placement-pdf-area">
                <div
                  ref={pageWrapperRef}
                  className={`signature-placement-page-wrapper ${placingComment ? 'signature-placement-modal-page-wrapper-placing' : ''}`}
                  onMouseDown={handlePageMouseDown}
                  onMouseMove={handlePageMouseMove}
                  onMouseLeave={handlePageMouseLeave}
                  onDragOver={handlePageDragOver}
                  onDragLeave={handlePageDragLeave}
                  onDrop={handlePageDrop}
                  style={{
                    cursor: placingComment || (selectedSigner && selectedFieldType) ? 'crosshair' : selectedField && !moving && !resizing ? 'move' : 'default',
                  }}
                >
                  {(dragPreview || cursorPreview || commentDragPreview) && (() => {
                    const p = commentDragPreview || dragPreview || cursorPreview;
                    const isCommentPreview = !!commentDragPreview || !!cursorPreview?.isComment;
                    return (
                      <div
                        className={
                          commentDragPreview
                            ? 'signature-placement-modal-drag-preview'
                            : cursorPreview?.isComment
                              ? 'signature-placement-modal-cursor-preview'
                              : dragPreview
                                ? 'signature-drag-preview'
                                : 'signature-cursor-preview'
                        }
                        style={{
                          left: `${p.positionX ?? p.position_x}%`,
                          top: `${p.positionY ?? p.position_y}%`,
                          width: `${p.width ?? COMMENT_DEFAULTS.width}%`,
                          height: `${p.height ?? COMMENT_DEFAULTS.height}%`,
                        }}
                      >
                        {!isCommentPreview && (
                          <span>
                            {getFieldTypeLabel((dragPreview || cursorPreview).fieldType || selectedFieldType)}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  <Document key={pdfData || 'doc'} file={fileConfig} onLoadSuccess={onDocumentLoadSuccess}>
                    <Page
                      pageNumber={currentPage}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      scale={pdfScale}
                    />
                  </Document>
                  <div className="signature-placement-fields-overlay">
                    {Object.entries(signatureFields).flatMap(([email, fields]) =>
                      (fields || [])
                        .filter((f) => f.identifier === fileIndex && f.page === currentPage)
                        .map((f) => {
                          const fieldLabel = getFieldTypeLabel(f.type);
                          const all = signatureFields[email] || [];
                          const idx = all.findIndex((x) => x === f || fieldsMatch(x, f));
                          const isSelected =
                            selectedField?.email === email && selectedField?.index === idx;
                          return (
                            <div
                              key={`overlay-${email}-${idx}-${f.page}-${f.positionX ?? f.position_x}-${f.positionY ?? f.position_y}`}
                              className={`signature-field-overlay-box${isSelected ? ' selected' : ''}`}
                              style={{
                                left: `${f.positionX ?? f.position_x ?? 0}%`,
                                top: `${f.positionY ?? f.position_y ?? 0}%`,
                                width: `${f.width || 25}%`,
                                height: `${f.height || 10}%`,
                              }}
                              title={isSelected ? (t('documenso.dragToMoveResizeHint') || `${fieldLabel} – Drag to move, drag corners to resize`) : `${email}: ${fieldLabel}`}
                              onMouseDown={isSelected && !disabled ? (e) => {
                                if (e.target.closest('.signature-field-resize-handle')) return;
                                handleFieldBodyMouseDown(e, email, idx, f);
                              } : undefined}
                            >
                              <span className="signature-field-overlay-label">{fieldLabel}</span>
                              {isSelected && !disabled && (
                                <>
                                  {['tl', 'tr', 'bl', 'br'].map((corner) => (
                                    <div
                                      key={corner}
                                      className={`signature-field-resize-handle signature-field-resize-${corner}`}
                                      onMouseDown={(ev) => handleFieldResizeMouseDown(ev, email, idx, f, corner)}
                                    />
                                  ))}
                                </>
                              )}
                            </div>
                          );
                        })
                    )}
                  </div>
                  {canComment && (
                    <div className="signature-placement-modal-comments-overlay">
                      {pendingPlacement && Number(pendingPlacement.page) === currentPage && (
                        <div
                          className={`signature-placement-modal-placeholder ${selectedPlacement ? 'selected' : ''}`}
                          style={{
                            left: `${pendingPlacement.position_x}%`,
                            top: `${pendingPlacement.position_y}%`,
                            width: `${pendingPlacement.width}%`,
                            height: `${pendingPlacement.height}%`,
                          }}
                          onMouseDown={(e) => handleCommentPlaceholderMouseDown(e, false)}
                          title={t('attachmentPreview.dragToMove') || 'Drag to move'}
                        >
                          <div className="signature-placement-modal-placeholder-editor" onClick={(e) => e.stopPropagation()}>
                            <CommentEditor
                              ref={placeholderInputRef}
                              value={commentDraft}
                              onChange={setCommentDraft}
                              placeholder={t('attachmentPreview.addComment') || 'Add a comment...'}
                              disabled={commentSubmitting}
                              toolbarSuffix={
                                <>
                                  <button
                                    type="button"
                                    className="signature-placement-modal-placeholder-trash"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDiscardPlacement();
                                    }}
                                    disabled={commentSubmitting}
                                    title={t('attachmentPreview.discardComment') || 'Discard'}
                                  >
                                    <DeleteOutlined />
                                  </button>
                                  <button
                                    type="button"
                                    className="signature-placement-modal-placeholder-save"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleAddComment();
                                    }}
                                    disabled={commentSubmitting || isContentEmpty(commentDraft)}
                                    title={t('attachmentPreview.saveComment') || 'Save comment'}
                                  >
                                    <ArrowRightOutlined />
                                  </button>
                                </>
                              }
                            />
                          </div>
                          {selectedPlacement && (
                            <>
                              {['tl', 'tr', 'bl', 'br'].map((corner) => (
                                <div
                                  key={corner}
                                  className={`signature-placement-modal-resize-handle signature-placement-modal-resize-${corner}`}
                                  onMouseDown={(ev) => handleCommentResizeMouseDown(ev, corner, false)}
                                />
                              ))}
                            </>
                          )}
                        </div>
                      )}
                      {editingDraft && selectedCommentId && (() => {
                        const comment = findInTree(currentPageCommentTree, selectedCommentId);
                        const isTopLevel = comment && !comment.parent_comment_id;
                        if (!comment || !isTopLevel) return null;
                        return (
                          <div
                            className="signature-placement-modal-placeholder selected"
                            style={{
                              left: `${editingDraft.position_x}%`,
                              top: `${editingDraft.position_y}%`,
                              width: `${editingDraft.width}%`,
                              height: `${editingDraft.height}%`,
                            }}
                            onMouseDown={(e) => handleCommentPlaceholderMouseDown(e, true)}
                            title={t('attachmentPreview.dragToMove') || 'Drag to move'}
                          >
                            <div className="signature-placement-modal-placeholder-editor" onClick={(e) => e.stopPropagation()}>
                              <CommentEditor
                                ref={editingInputRef}
                                value={editingDraft.content}
                                onChange={(html) => updateEditingDraft({ content: html })}
                                placeholder={t('attachmentPreview.addComment') || 'Add a comment...'}
                                disabled={commentSubmitting}
                                toolbarSuffix={
                                  <>
                                    <button
                                      type="button"
                                      className="signature-placement-modal-placeholder-close"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedCommentId(null);
                                        setEditingDraft(null);
                                      }}
                                      disabled={commentSubmitting}
                                      title={t('common.close') || 'Close'}
                                    >
                                      <CloseOutlined />
                                    </button>
                                    <button
                                      type="button"
                                      className="signature-placement-modal-placeholder-trash"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDeleteComment();
                                      }}
                                      disabled={commentSubmitting}
                                      title={t('attachmentPreview.deleteComment') || 'Delete comment'}
                                    >
                                      <DeleteOutlined />
                                    </button>
                                    <button
                                      type="button"
                                      className="signature-placement-modal-placeholder-save"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleSaveEditedComment();
                                      }}
                                      disabled={commentSubmitting || isContentEmpty(editingDraft.content)}
                                      title={t('attachmentPreview.saveComment') || 'Save comment'}
                                    >
                                      <ArrowRightOutlined />
                                    </button>
                                  </>
                                }
                              />
                            </div>
                            {['tl', 'tr', 'bl', 'br'].map((corner) => (
                              <div
                                key={corner}
                                className={`signature-placement-modal-resize-handle signature-placement-modal-resize-${corner}`}
                                onMouseDown={(ev) => handleCommentResizeMouseDown(ev, corner, true)}
                              />
                            ))}
                          </div>
                        );
                      })()}
                      {postitsVisible && currentPageCommentTree.map((thread) => {
                        const authorColor = getAuthorColor(thread);
                        return (
                          <div key={thread.unique_identifier} className={`signature-placement-modal-comment-thread${replyingToCommentId === thread.unique_identifier ? ' signature-placement-modal-comment-thread-replying' : ''}`}>
                            {selectedCommentId !== thread.unique_identifier && (
                              <div
                                className="signature-placement-modal-postit-wrapper"
                                style={{
                                  left: `${thread.position_x ?? 50}%`,
                                  top: `${thread.position_y ?? 50}%`,
                                  width: `${thread.width ?? COMMENT_DEFAULTS.width}%`,
                                  height: `${thread.height ?? COMMENT_DEFAULTS.height}%`,
                                }}
                              >
                                <div
                                  className={`signature-placement-modal-postit${!canEditComment(thread) ? ' signature-placement-modal-postit-readonly' : ''}`}
                                  style={{
                                    cursor: canEditComment(thread) ? undefined : 'default',
                                    '--signature-modal-postit-border': authorColor.border,
                                    '--signature-modal-postit-accent': authorColor.accent,
                                  }}
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    if (canEditComment(thread)) handleCommentMarkerMouseDown(thread, e);
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (commentsPanelRef.current) {
                                      const el = threadRefsMap.current[thread.unique_identifier];
                                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                    }
                                  }}
                                >
                                  {renderThreadContent(thread, authorColor.border)}
                                  {canComment && (
                                    <button
                                      type="button"
                                      className="signature-placement-modal-postit-reply-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openReplyTo(thread.unique_identifier);
                                      }}
                                      title={t('attachmentPreview.replyToComment') || 'Reply'}
                                      tabIndex={-1}
                                    >
                                      <CommentOutlined />
                                      <span>{t('attachmentPreview.reply') || 'Reply'}</span>
                                    </button>
                                  )}
                                </div>
                                {replyingToCommentId === thread.unique_identifier && (
                                  <div className="signature-placement-modal-postit-reply-form">
                                    <CommentEditor
                                      value={replyDraft}
                                      onChange={setReplyDraft}
                                      placeholder={t('attachmentPreview.writeReply') || 'Write a reply...'}
                                      disabled={commentSubmitting}
                                      toolbarSuffix={
                                        <>
                                          <button type="button" className="signature-placement-modal-placeholder-trash" tabIndex={-1} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReplyingToCommentId(null); setReplyDraft(''); }} disabled={commentSubmitting} title={t('attachmentPreview.cancelReply') || 'Cancel'}>
                                            <CloseOutlined />
                                          </button>
                                          <button type="button" className="signature-placement-modal-placeholder-save" tabIndex={-1} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddReply(thread.unique_identifier, replyDraft); }} disabled={commentSubmitting || isContentEmpty(replyDraft)} title={t('attachmentPreview.sendReply') || 'Send reply'}>
                                            <ArrowRightOutlined />
                                          </button>
                                        </>
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {canViewComments && (
                <div className={`signature-placement-modal-comments-panel ${sidebarCollapsed ? 'signature-placement-modal-comments-panel-collapsed' : ''}`} ref={commentsPanelRef}>
                  <div className="signature-placement-modal-comments-panel-header">
                    <span className="signature-placement-modal-comments-panel-title">
                      {t('attachmentPreview.comments') || 'Comments'}
                      {commentTree.length > 0 && (
                        <span className="signature-placement-modal-comments-count"> ({commentTree.length})</span>
                      )}
                    </span>
                    <button
                      type="button"
                      className="signature-placement-modal-comments-panel-close"
                      tabIndex={-1}
                      onClick={() => setSidebarCollapsed(true)}
                      title={t('attachmentPreview.hideComments') || 'Hide comments'}
                    >
                      <MenuFoldOutlined />
                    </button>
                  </div>
                  {!sidebarCollapsed && (
                    <div className="signature-placement-modal-threads">
                      {commentTree.length === 0 && !pendingPlacement && (
                        <div className="signature-placement-modal-threads-empty">
                          {canComment
                            ? (t('attachmentPreview.noCommentsYet') || 'No comments yet. Click "Add comment" to place one on the document.')
                            : (t('attachmentPreview.noComments') || 'No comments yet.')}
                        </div>
                      )}
                      {commentTree.map((thread) => {
                        const threadAuthorColor = getAuthorColor(thread);
                        const renderReply = (reply, depth) => {
                          const replyAuthorColor = getAuthorColor(reply);
                          return (
                            <div key={reply.unique_identifier} className={`signature-placement-modal-thread-reply signature-placement-modal-thread-reply-depth-${depth}`} style={{ '--signature-modal-reply-accent': replyAuthorColor.accent }}>
                              {selectedCommentId === reply.unique_identifier && editingDraft ? (
                                <div className="signature-placement-modal-thread-edit">
                                  <CommentEditor
                                    ref={editingInputRef}
                                    value={editingDraft.content}
                                    onChange={(html) => updateEditingDraft({ content: html })}
                                    placeholder={t('attachmentPreview.addComment') || 'Add a comment...'}
                                    disabled={commentSubmitting}
                                    toolbarSuffix={
                                      <>
                                        <button type="button" className="signature-placement-modal-placeholder-close" tabIndex={-1} onClick={() => { setSelectedCommentId(null); setEditingDraft(null); }} disabled={commentSubmitting} title={t('common.close') || 'Close'}>
                                          <CloseOutlined />
                                        </button>
                                        <button type="button" className="signature-placement-modal-placeholder-trash" tabIndex={-1} onClick={(e) => { e.preventDefault(); handleDeleteComment(); }} disabled={commentSubmitting} title={t('attachmentPreview.deleteComment') || 'Delete comment'}>
                                          <DeleteOutlined />
                                        </button>
                                        <button type="button" className="signature-placement-modal-placeholder-save" tabIndex={-1} onClick={(e) => { e.preventDefault(); handleSaveEditedComment(); }} disabled={commentSubmitting || isContentEmpty(editingDraft.content)} title={t('attachmentPreview.saveComment') || 'Save comment'}>
                                          <ArrowRightOutlined />
                                        </button>
                                      </>
                                    }
                                  />
                                </div>
                              ) : (
                                <div className={`signature-placement-modal-thread-record${!isLoggedIn ? ' signature-placement-modal-thread-record-no-actions' : ''}`}>
                                  <div className="signature-placement-modal-thread-reply-content" dangerouslySetInnerHTML={{ __html: reply.content || '' }} />
                                  {isLoggedIn && (
                                    <div className="signature-placement-modal-thread-actions">
                                      {canEditComment(reply) && (
                                        <button type="button" className="signature-placement-modal-thread-action-icon" onClick={() => handleSelectComment(reply)} title={t('attachmentPreview.editComment') || 'Edit'} tabIndex={-1}>
                                          <EditOutlined />
                                        </button>
                                      )}
                                      {canReplyToComment && (
                                        <button type="button" className="signature-placement-modal-thread-action-icon" onClick={() => openReplyTo(reply.unique_identifier)} disabled={commentSubmitting} title={t('attachmentPreview.replyToComment') || 'Reply'} tabIndex={-1}>
                                          <CommentOutlined />
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              {replyingToCommentId === reply.unique_identifier && (
                                <div className={`signature-placement-modal-thread-reply-form ${depth > 0 ? 'signature-placement-modal-thread-reply-form-nested' : ''}`}>
                                  <CommentEditor
                                    value={replyDraft}
                                    onChange={setReplyDraft}
                                    placeholder={t('attachmentPreview.writeReply') || 'Write a reply...'}
                                    disabled={commentSubmitting}
                                    toolbarSuffix={
                                      <>
                                        <button type="button" className="signature-placement-modal-placeholder-trash" tabIndex={-1} onClick={(e) => { e.preventDefault(); setReplyingToCommentId(null); setReplyDraft(''); }} disabled={commentSubmitting} title={t('attachmentPreview.cancelReply') || 'Cancel'}>
                                          <CloseOutlined />
                                        </button>
                                        <button type="button" className="signature-placement-modal-placeholder-save" tabIndex={-1} onClick={(e) => { e.preventDefault(); handleAddReply(reply.unique_identifier, replyDraft); }} disabled={commentSubmitting || isContentEmpty(replyDraft)} title={t('attachmentPreview.sendReply') || 'Send reply'}>
                                          <ArrowRightOutlined />
                                        </button>
                                      </>
                                    }
                                  />
                                </div>
                              )}
                              {(reply.replies ?? []).map((nested) => renderReply(nested, depth + 1))}
                            </div>
                          );
                        };
                        return (
                          <div
                            key={thread.unique_identifier}
                            ref={(el) => { if (el) threadRefsMap.current[thread.unique_identifier] = el; }}
                            className={`signature-placement-modal-thread-card ${selectedCommentId === thread.unique_identifier ? 'signature-placement-modal-thread-card-selected' : ''}`}
                            style={{ '--thread-accent': threadAuthorColor.accent }}
                          >
                            <div className="signature-placement-modal-thread-parent">
                              {selectedCommentId === thread.unique_identifier && editingDraft ? (
                                <div className="signature-placement-modal-thread-edit">
                                  <CommentEditor
                                    ref={editingInputRef}
                                    value={editingDraft.content}
                                    onChange={(html) => updateEditingDraft({ content: html })}
                                    placeholder={t('attachmentPreview.addComment') || 'Add a comment...'}
                                    disabled={commentSubmitting}
                                    toolbarSuffix={
                                      <>
                                        <button type="button" className="signature-placement-modal-placeholder-close" tabIndex={-1} onClick={() => { setSelectedCommentId(null); setEditingDraft(null); }} disabled={commentSubmitting} title={t('common.close') || 'Close'}>
                                          <CloseOutlined />
                                        </button>
                                        <button type="button" className="signature-placement-modal-placeholder-trash" tabIndex={-1} onClick={(e) => { e.preventDefault(); handleDeleteComment(); }} disabled={commentSubmitting} title={t('attachmentPreview.deleteComment') || 'Delete comment'}>
                                          <DeleteOutlined />
                                        </button>
                                        <button type="button" className="signature-placement-modal-placeholder-save" tabIndex={-1} onClick={(e) => { e.preventDefault(); handleSaveEditedComment(); }} disabled={commentSubmitting || isContentEmpty(editingDraft.content)} title={t('attachmentPreview.saveComment') || 'Save comment'}>
                                          <ArrowRightOutlined />
                                        </button>
                                      </>
                                    }
                                  />
                                </div>
                              ) : (
                                <div className={`signature-placement-modal-thread-record${!isLoggedIn ? ' signature-placement-modal-thread-record-no-actions' : ''}`}>
                                  <div className="signature-placement-modal-thread-content" dangerouslySetInnerHTML={{ __html: thread.content || '' }} />
                                  {isLoggedIn && (
                                    <div className="signature-placement-modal-thread-actions">
                                      {canEditComment(thread) && (
                                        <button type="button" className="signature-placement-modal-thread-action-icon" onClick={() => handleSelectComment(thread)} title={t('attachmentPreview.editComment') || 'Edit'} tabIndex={-1}>
                                          <EditOutlined />
                                        </button>
                                      )}
                                      {canReplyToComment && (
                                        <button type="button" className="signature-placement-modal-thread-action-icon" onClick={() => openReplyTo(thread.unique_identifier)} disabled={commentSubmitting} title={t('attachmentPreview.replyToComment') || 'Reply'} tabIndex={-1}>
                                          <CommentOutlined />
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            {replyingToCommentId === thread.unique_identifier && (
                              <div className="signature-placement-modal-thread-reply-form">
                                <CommentEditor
                                  value={replyDraft}
                                  onChange={setReplyDraft}
                                  placeholder={t('attachmentPreview.writeReply') || 'Write a reply...'}
                                  disabled={commentSubmitting}
                                  toolbarSuffix={
                                    <>
                                      <button type="button" className="signature-placement-modal-placeholder-trash" tabIndex={-1} onClick={(e) => { e.preventDefault(); setReplyingToCommentId(null); setReplyDraft(''); }} disabled={commentSubmitting} title={t('attachmentPreview.cancelReply') || 'Cancel'}>
                                        <CloseOutlined />
                                      </button>
                                      <button type="button" className="signature-placement-modal-placeholder-save" tabIndex={-1} onClick={(e) => { e.preventDefault(); handleAddReply(thread.unique_identifier, replyDraft); }} disabled={commentSubmitting || isContentEmpty(replyDraft)} title={t('attachmentPreview.sendReply') || 'Send reply'}>
                                        <ArrowRightOutlined />
                                      </button>
                                    </>
                                  }
                                />
                              </div>
                            )}
                            {(thread.replies ?? []).map((reply) => renderReply(reply, 0))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>

        <div className="signature-placement-fields-summary">
          {Object.entries(signatureFields)
            .flatMap(([email, fields]) =>
              (fields || [])
                .filter((f) => f.identifier === fileIndex)
                .map((f) => ({ email, field: f }))
            )
            .sort((a, b) => {
              const oa = a.field.order;
              const ob = b.field.order;
              if (oa != null && ob != null) return oa - ob;
              if (oa != null) return -1;
              if (ob != null) return 1;
              return (a.field.page - b.field.page) || (a.field.positionY - b.field.positionY) || (a.field.positionX - b.field.positionX);
            })
            .map(({ email, field: f }, i) => {
              const all = signatureFields[email] || [];
              const idx = all.findIndex((x) => x === f || fieldsMatch(x, f));
              const isTagSelected = selectedField?.email === email && selectedField?.index === idx;
              const fieldTypeLabel = getFieldTypeLabel(f.type);
              const tooltipText = `${fieldTypeLabel} — ${t('documenso.page') || 'Page'} ${f.page} @ (${Math.round(f.positionX)}, ${Math.round(f.positionY)})`;
              return (
                <Tooltip key={`${email}-${i}-${f.page}-${f.positionX}-${f.positionY}`} title={tooltipText}>
                  <div
                    className={`signature-field-tag${isTagSelected ? ' signature-field-tag-selected' : ''}${!disabled ? ' signature-field-tag-clickable' : ''}`}
                    onClick={!disabled ? () => {
                      if (isTagSelected) {
                        setSelectedField(null);
                      } else {
                        setSelectedField({ email, index: idx });
                        setCurrentPage(f.page);
                      }
                    } : undefined}
                  >
                    <span className="signature-field-tag-text">{email}</span>
                    <span className="signature-field-tag-icon">{getFieldTypeIcon(f.type)}</span>
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFieldByRef(email, f);
                      }}
                      disabled={disabled}
                    />
                  </div>
                </Tooltip>
              );
            })}
          {Object.values(signatureFields).flat().filter((f) => f?.identifier === fileIndex).length === 0 && (
            <span className="signature-placement-fields-empty">
              {t('documenso.noFieldsPlaced') || 'No fields placed yet'}
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SignaturePlacementModal;
